// Page Outils, onglet MCP (DeployMCPSection), AGENT-832. Monté sous la clé
// `mcpTools`. Vocabulaire externe (naming-conventions) : un deployment est un
// « Agent » ; un grant cross-deployment est un « accès » à l'instance d'un
// autre Agent, filtré par des « règles » (policies allow/deny).
// L'espagnol retombe sur le français (fallbackLng) tant qu'il n'est pas traduit.
export const mcpToolsResources = {
  fr: {
    overview: {
      badge: 'MCP dynamiques',
      runtimeTargetBadge: 'Cible d’exécution : {{target}}',
      title: 'MCP dynamiques',
      description:
        'Gérez ici les serveurs MCP de cet Agent : création et modification, tâches d’exécution, et accès aux instances des autres Agents de l’organisation.',
      createOAuth: 'Créer un MCP OAuth',
      create: 'Créer un MCP',
      runtimeTarget: 'Cible d’exécution',
      managedProviders: 'Fournisseurs gérés disponibles : {{providers}}',
      ownedTitle: 'Instances de cet Agent',
      ownedHint:
        'Création manuelle en SSE ou STDIO, ou provisionnement géré (binary, docker, k8s ou koyeb) selon la cible.',
      grantsTitle: 'Accès reçus',
      grantsHint:
        'Instances d’autres Agents attachées par un accès explicite, filtré par des règles d’autorisation ou d’interdiction.',
    },
    owned: {
      title: 'Instances de cet Agent',
      description:
        'Instances MCP qui appartiennent à cet Agent. « Redéployer » relance une instance et rafraîchit ses outils dans le contexte de l’Agent.',
      redeployAll: 'Tout redéployer',
      redeploying: 'Redéploiement…',
      loading: 'Chargement des MCP…',
      empty: 'Aucune instance MCP pour cet Agent.',
      runtimeSecrets: 'secrets d’exécution',
      oauthBadge: 'OAuth : {{status}}',
      revision: 'Révision {{revision}}',
      endpoint: 'Endpoint : {{url}}',
      notSet: 'non renseigné',
      jobsAudit: 'Tâches et audit',
      edit: 'Modifier',
      oauth: 'OAuth',
      redeploy: 'Redéployer',
      delete: 'Supprimer',
      noRuntimeMessage: 'Aucun message d’exécution',
      technicalDetails: 'Détails techniques',
    },
    status: {
      active: 'actif',
      provisioning: 'en provisionnement',
      failed: 'en échec',
      inactive: 'inactif',
      pending: 'en attente',
      revoked: 'révoqué',
    },
    provisioning: {
      registryAuth: 'Image Docker inaccessible : le registre exige une authentification (docker login).',
      noManifest: 'Image indisponible pour l’architecture de cette machine.',
      imageNotFound: 'Image Docker introuvable.',
      dockerRunFailed: 'Le démarrage du conteneur Docker a échoué.',
      failed: 'Le provisionnement a échoué.',
    },
    oauthStatus: {
      notConfigured: 'non configuré',
      configured: 'configuré',
      insufficientScope: 'autorisation requise',
      reconnectRequired: 'reconnexion requise',
      providerNonCompliant: 'fournisseur non conforme',
      error: 'erreur',
      expired: 'expiré',
      connected: 'connecté',
    },
    grants: {
      title: 'Accès aux instances d’autres Agents',
      description:
        'Attachez une instance d’un autre Agent de l’organisation, puis définissez ici ses règles d’accès.',
      add: 'Ajouter un accès',
      empty: 'Aucun accès à une instance d’un autre Agent.',
      fallbackName: 'Accès',
      externalAgent: 'Agent externe',
      defaultNamespace: 'Namespace par défaut : {{namespace}}',
      emptyNamespace: '(vide)',
      deletePolicy: 'Supprimer la règle',
      noPolicy: 'Aucune règle pour cet accès.',
      addPolicy: 'Ajouter une règle',
      remove: 'Retirer l’accès',
    },
    accessMode: {
      read_write: 'lecture et écriture',
      read_only: 'lecture seule',
    },
    effect: {
      allow: 'Autoriser',
      deny: 'Interdire',
    },
    instanceDialog: {
      editTitle: 'Modifier le MCP',
      createTitle: 'Créer un MCP',
      description:
        'Créez un MCP manuel en SSE ou STDIO, ou un MCP géré compatible avec la cible {{target}}.',
      name: 'Nom',
      runtimeKind: 'Type d’exécution',
      runtimeKindManaged: 'géré',
      provider: 'Fournisseur',
      providerAria: 'Fournisseur géré',
      descriptionLabel: 'Description',
      endpointUrl: 'URL de l’endpoint',
      headers: 'En-têtes (un par ligne)',
      command: 'Commande',
      arguments: 'Arguments (un par ligne)',
      env: 'Variables d’environnement (une par ligne, KEY=VALUE)',
      binaryPath: 'Chemin du binaire (facultatif)',
      binaryPathAria: 'Chemin du binaire',
      catalogImage: 'Image du catalogue',
      catalogImagePlaceholder: 'Choisir une image du catalogue',
      catalogImageHint:
        'Choisissez une image du catalogue AgentMaurice, ou laissez vide pour saisir une image manuellement.',
      catalogImageError: 'Impossible de charger le catalogue des images gérées.',
      resolvedImage: 'Image retenue',
      manualImage: 'Image manuelle',
      manualImageHint: 'N’utilisez ce champ que si l’image n’est pas encore dans le registre.',
      publicEndpoint: 'Endpoint public',
      mcpPath: 'Chemin MCP',
      launchArguments: 'Arguments de lancement',
      plainEnv: 'Variables d’environnement non secrètes',
      runtimeSecrets: 'Secrets d’exécution',
      runtimeSecretsKeepPlaceholder: 'Secrets déjà configurés : saisissez seulement les valeurs à remplacer',
      runtimeSecretsHint:
        'Les secrets sont chiffrés côté serveur et ne sont plus jamais affichés après l’enregistrement.',
      cancel: 'Annuler',
      update: 'Mettre à jour',
      create: 'Créer',
      gmailDescription: 'Serveur MCP Gmail géré par AgentMaurice.',
    },
    oauthDialog: {
      title: 'Authentification OAuth du MCP',
      fallbackInstance: 'Instance MCP',
      statusBadge: 'OAuth : {{status}}',
      secretConfigured: 'Secret configuré',
      scopesNeeded: 'Des autorisations supplémentaires sont nécessaires.',
      scopesNeededList: 'Des autorisations supplémentaires sont nécessaires : {{scopes}}',
      grantScopes: 'Accorder les scopes requis',
      enabled: 'OAuth activé',
      clientId: 'ID client',
      clientIdAria: 'ID client OAuth',
      clientSecret: 'Secret client',
      clientSecretAria: 'Secret client OAuth',
      newSecret: 'Nouveau secret',
      replaceSecret: 'Remplacer ou supprimer le secret existant',
      metadataUrl: 'URL des métadonnées OAuth',
      scopes: 'Scopes',
      scopesAria: 'Scopes OAuth',
      callbackUrl: 'URL de rappel',
      callbackUrlAria: 'URL de rappel OAuth',
      configure: 'Configurer OAuth',
      disconnect: 'Déconnecter',
      reconnect: 'Reconnecter',
      connect: 'Connecter le compte',
    },
    grantDialog: {
      title: 'Ajouter un accès',
      description: 'Choisissez une instance d’un autre Agent de l’organisation.',
      instance: 'Instance à attacher',
      instanceAria: 'Instance du catalogue',
      instancePlaceholder: 'Choisir une instance',
      fallbackAgent: 'Agent',
      defaultNamespace: 'Namespace par défaut',
      accessMode: 'Mode d’accès',
      cancel: 'Annuler',
      attach: 'Attacher',
    },
    policyDialog: {
      title: 'Ajouter une règle',
      description: 'Règle appliquée à l’accès {{name}}.',
      namespace: 'Namespace',
      namespaceAria: 'Namespace de la règle',
      effect: 'Effet',
      effectAria: 'Effet de la règle',
      priority: 'Priorité',
      priorityAria: 'Priorité de la règle',
      actions: 'Actions (une par ligne)',
      actionsAria: 'Actions de la règle',
      cancel: 'Annuler',
      create: 'Créer la règle',
    },
    detailsDialog: {
      title: 'Tâches, santé et audit',
      jobs: 'Tâches de provisionnement',
      noJobs: 'Aucune tâche pour cette instance.',
      audit: 'Audit',
      noNamespace: 'namespace non renseigné',
      noAudit: 'Aucun événement d’audit pour cette instance.',
    },
    toast: {
      instanceUpdated: 'MCP mis à jour',
      instanceCreated: 'MCP créé',
      instanceSaveError: 'Impossible d’enregistrer ce MCP',
      instanceDeleted: 'MCP supprimé',
      instanceDeleteError: 'Suppression impossible',
      redeployStarted: 'Redéploiement lancé',
      redeployStartedMany: 'Redéploiement lancé pour {{count}} instances',
      redeployPartial: 'Redéploiement partiel : {{started}} sur {{total}} lancés',
      redeployError: 'Redéploiement impossible',
      redeployAllError: 'Redéploiement impossible pour toutes les instances',
      grantCreated: 'Accès ajouté',
      grantCreateError: 'Impossible d’ajouter cet accès',
      grantDeleted: 'Accès retiré',
      grantDeleteError: 'Impossible de retirer cet accès',
      noGrantSelected: 'Aucun accès sélectionné',
      policyCreated: 'Règle créée',
      policyCreateError: 'Impossible de créer la règle',
      policyDeleted: 'Règle supprimée',
      policyDeleteError: 'Impossible de supprimer la règle',
      noInstanceSelected: 'Aucune instance MCP sélectionnée',
      oauthSaved: 'Configuration OAuth enregistrée',
      oauthSaveError: 'Configuration OAuth impossible',
      oauthStarted: 'Connexion OAuth lancée',
      oauthStartError: 'Connexion OAuth impossible',
      oauthDisconnected: 'Compte OAuth déconnecté',
      oauthDisconnectError: 'Déconnexion OAuth impossible',
      nameRequired: 'Le nom est requis',
      sseUrlRequired: 'L’URL SSE est requise',
      stdioCommandRequired: 'La commande STDIO est requise',
      imageRequired: 'Choisissez une image du catalogue ou saisissez une image',
      instanceRequired: 'Choisissez une instance à attacher',
      namespaceRequired: 'Le namespace est requis',
    },
  },
  en: {
    overview: {
      badge: 'Dynamic MCP',
      runtimeTargetBadge: 'Runtime target: {{target}}',
      title: 'Dynamic MCP',
      description:
        'Manage this Agent’s MCP servers here: creation and editing, runtime jobs, and access to the instances of the organization’s other Agents.',
      createOAuth: 'Create an OAuth MCP',
      create: 'Create an MCP',
      runtimeTarget: 'Runtime target',
      managedProviders: 'Available managed providers: {{providers}}',
      ownedTitle: 'This Agent’s instances',
      ownedHint:
        'Manual creation over SSE or STDIO, or managed provisioning (binary, docker, k8s or koyeb) depending on the target.',
      grantsTitle: 'Incoming access',
      grantsHint: 'Instances of other Agents attached through explicit access, filtered by allow or deny rules.',
    },
    owned: {
      title: 'This Agent’s instances',
      description:
        'MCP instances owned by this Agent. “Redeploy” restarts an instance and refreshes its tools in the Agent’s context.',
      redeployAll: 'Redeploy all',
      redeploying: 'Redeploying…',
      loading: 'Loading MCP…',
      empty: 'No MCP instance for this Agent.',
      runtimeSecrets: 'runtime secrets',
      oauthBadge: 'OAuth: {{status}}',
      revision: 'Revision {{revision}}',
      endpoint: 'Endpoint: {{url}}',
      notSet: 'not set',
      jobsAudit: 'Jobs and audit',
      edit: 'Edit',
      oauth: 'OAuth',
      redeploy: 'Redeploy',
      delete: 'Delete',
      noRuntimeMessage: 'No runtime message',
      technicalDetails: 'Technical details',
    },
    status: {
      active: 'active',
      provisioning: 'provisioning',
      failed: 'failed',
      inactive: 'inactive',
      pending: 'pending',
      revoked: 'revoked',
    },
    provisioning: {
      registryAuth: 'Docker image unreachable: the registry requires authentication (docker login).',
      noManifest: 'Image not available for this machine’s architecture.',
      imageNotFound: 'Docker image not found.',
      dockerRunFailed: 'The Docker container failed to start.',
      failed: 'Provisioning failed.',
    },
    oauthStatus: {
      notConfigured: 'not configured',
      configured: 'configured',
      insufficientScope: 'authorization required',
      reconnectRequired: 'reconnection required',
      providerNonCompliant: 'non-compliant provider',
      error: 'error',
      expired: 'expired',
      connected: 'connected',
    },
    grants: {
      title: 'Access to other Agents’ instances',
      description: 'Attach an instance from another Agent of the organization, then set its access rules here.',
      add: 'Add access',
      empty: 'No access to another Agent’s instance.',
      fallbackName: 'Access',
      externalAgent: 'External Agent',
      defaultNamespace: 'Default namespace: {{namespace}}',
      emptyNamespace: '(empty)',
      deletePolicy: 'Delete rule',
      noPolicy: 'No rule for this access.',
      addPolicy: 'Add rule',
      remove: 'Remove access',
    },
    accessMode: {
      read_write: 'read and write',
      read_only: 'read only',
    },
    effect: {
      allow: 'Allow',
      deny: 'Deny',
    },
    instanceDialog: {
      editTitle: 'Edit MCP',
      createTitle: 'Create an MCP',
      description: 'Create a manual MCP over SSE or STDIO, or a managed MCP compatible with the {{target}} target.',
      name: 'Name',
      runtimeKind: 'Runtime kind',
      runtimeKindManaged: 'managed',
      provider: 'Provider',
      providerAria: 'Managed provider',
      descriptionLabel: 'Description',
      endpointUrl: 'Endpoint URL',
      headers: 'Headers (one per line)',
      command: 'Command',
      arguments: 'Arguments (one per line)',
      env: 'Environment variables (one per line, KEY=VALUE)',
      binaryPath: 'Binary path (optional)',
      binaryPathAria: 'Binary path',
      catalogImage: 'Catalog image',
      catalogImagePlaceholder: 'Choose a catalog image',
      catalogImageHint: 'Choose an image from the AgentMaurice catalog, or leave empty to enter an image manually.',
      catalogImageError: 'Could not load the managed image catalog.',
      resolvedImage: 'Resolved image',
      manualImage: 'Manual image',
      manualImageHint: 'Only use this field if the image is not in the registry yet.',
      publicEndpoint: 'Public endpoint',
      mcpPath: 'MCP path',
      launchArguments: 'Launch arguments',
      plainEnv: 'Non-secret environment variables',
      runtimeSecrets: 'Runtime secrets',
      runtimeSecretsKeepPlaceholder: 'Secrets already set: enter only the values to replace',
      runtimeSecretsHint: 'Secrets are encrypted server-side and never shown again after saving.',
      cancel: 'Cancel',
      update: 'Update',
      create: 'Create',
      gmailDescription: 'Gmail MCP server managed by AgentMaurice.',
    },
    oauthDialog: {
      title: 'MCP OAuth authentication',
      fallbackInstance: 'MCP instance',
      statusBadge: 'OAuth: {{status}}',
      secretConfigured: 'Secret set',
      scopesNeeded: 'Additional permissions are required.',
      scopesNeededList: 'Additional permissions are required: {{scopes}}',
      grantScopes: 'Grant the required scopes',
      enabled: 'OAuth enabled',
      clientId: 'Client ID',
      clientIdAria: 'OAuth client ID',
      clientSecret: 'Client secret',
      clientSecretAria: 'OAuth client secret',
      newSecret: 'New secret',
      replaceSecret: 'Replace or delete the existing secret',
      metadataUrl: 'OAuth metadata URL',
      scopes: 'Scopes',
      scopesAria: 'OAuth scopes',
      callbackUrl: 'Callback URL',
      callbackUrlAria: 'OAuth callback URL',
      configure: 'Configure OAuth',
      disconnect: 'Disconnect',
      reconnect: 'Reconnect',
      connect: 'Connect the account',
    },
    grantDialog: {
      title: 'Add access',
      description: 'Choose an instance from another Agent of the organization.',
      instance: 'Instance to attach',
      instanceAria: 'Catalog instance',
      instancePlaceholder: 'Choose an instance',
      fallbackAgent: 'Agent',
      defaultNamespace: 'Default namespace',
      accessMode: 'Access mode',
      cancel: 'Cancel',
      attach: 'Attach',
    },
    policyDialog: {
      title: 'Add rule',
      description: 'Rule applied to the {{name}} access.',
      namespace: 'Namespace',
      namespaceAria: 'Rule namespace',
      effect: 'Effect',
      effectAria: 'Rule effect',
      priority: 'Priority',
      priorityAria: 'Rule priority',
      actions: 'Actions (one per line)',
      actionsAria: 'Rule actions',
      cancel: 'Cancel',
      create: 'Create rule',
    },
    detailsDialog: {
      title: 'Jobs, health and audit',
      jobs: 'Provisioning jobs',
      noJobs: 'No job for this instance.',
      audit: 'Audit',
      noNamespace: 'no namespace',
      noAudit: 'No audit event for this instance.',
    },
    toast: {
      instanceUpdated: 'MCP updated',
      instanceCreated: 'MCP created',
      instanceSaveError: 'Could not save this MCP',
      instanceDeleted: 'MCP deleted',
      instanceDeleteError: 'Could not delete',
      redeployStarted: 'Redeployment started',
      redeployStartedMany: 'Redeployment started for {{count}} instances',
      redeployPartial: 'Partial redeployment: {{started}} of {{total}} started',
      redeployError: 'Could not redeploy',
      redeployAllError: 'Could not redeploy any instance',
      grantCreated: 'Access added',
      grantCreateError: 'Could not add this access',
      grantDeleted: 'Access removed',
      grantDeleteError: 'Could not remove this access',
      noGrantSelected: 'No access selected',
      policyCreated: 'Rule created',
      policyCreateError: 'Could not create the rule',
      policyDeleted: 'Rule deleted',
      policyDeleteError: 'Could not delete the rule',
      noInstanceSelected: 'No MCP instance selected',
      oauthSaved: 'OAuth configuration saved',
      oauthSaveError: 'Could not configure OAuth',
      oauthStarted: 'OAuth connection started',
      oauthStartError: 'Could not start the OAuth connection',
      oauthDisconnected: 'OAuth account disconnected',
      oauthDisconnectError: 'Could not disconnect OAuth',
      nameRequired: 'The name is required',
      sseUrlRequired: 'The SSE URL is required',
      stdioCommandRequired: 'The STDIO command is required',
      imageRequired: 'Choose a catalog image or enter an image',
      instanceRequired: 'Choose an instance to attach',
      namespaceRequired: 'The namespace is required',
    },
  },
}
